// Banner de estado da conexão.
//
// Duas informações diferentes, e é importante não misturar:
//
//  • RISCO — permanente enquanto a conexão for não-oficial. Não é alarme, é
//    contexto: o número pode ser banido sem aviso.
//  • ESTADO — a conexão está de pé agora? Aqui entram o `status` da conexão
//    (heartbeat do backend, que marca erro depois de 5 min sem sinal) e o
//    estado do canal de tempo real do navegador. São coisas distintas: dá para
//    o WhatsApp estar conectado e o realtime da aba ter caído — e nesse caso a
//    tela está desatualizada sem parecer.
//
// Nada aqui pergunta `channel_type === 'whatsapp_evolution'`. Quem diz que o
// canal é arriscado é a capability `isUnofficial`, que veio junto da conexão.

import React from 'react';
import { AlertTriangle, RefreshCw, ShieldAlert, WifiOff } from 'lucide-react';
import type { ConexaoPublica } from '../tipos';
import type { EstadoRealtime } from '../useInboxRealtime';
import { desdeQuando } from '../formato';

interface Props {
  conexoes: ConexaoPublica[];
  realtime: EstadoRealtime;
  onReconectar: () => void;
}

export default function BannerConexao({ conexoes, realtime, onReconectar }: Props) {
  const naoOficiais = conexoes.filter((c) => c.capabilities?.isUnofficial);
  const comProblema = conexoes.filter((c) => c.status === 'error' || c.status === 'disconnected');
  const aguardandoQr = conexoes.filter((c) => c.status === 'qr_pending' || c.status === 'pending');

  return (
    <div className="flex flex-col gap-2">
      {comProblema.map((c) => (
        <div
          key={c.id}
          className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm dark:border-red-900/50 dark:bg-red-900/20"
        >
          <WifiOff size={16} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-red-800 dark:text-red-300">
              {c.display_name} está fora do ar
            </p>
            <p className="text-red-700/90 dark:text-red-400/90">
              {c.last_error || 'A conexão caiu.'} Último sinal {desdeQuando(c.last_seen_at)}. Mensagem
              nova não entra e resposta não sai enquanto isso.
            </p>
          </div>
          <button
            onClick={onReconectar}
            className="shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/40"
          >
            Verificar
          </button>
        </div>
      ))}

      {aguardandoQr.map((c) => (
        <div
          key={c.id}
          className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-900/20"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-amber-800 dark:text-amber-300">
            <span className="font-semibold">{c.display_name}</span> ainda não foi pareada. O QR code
            fica na gestão de conexões.
          </p>
        </div>
      ))}

      {realtime === 'caiu' && (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-900/20">
          <RefreshCw size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-amber-800 dark:text-amber-300">
              Esta aba parou de receber mensagem na hora
            </p>
            <p className="text-amber-700/90 dark:text-amber-400/90">
              O WhatsApp pode estar funcionando normalmente — quem caiu foi a conexão desta tela.
              Atualize para voltar a ver mensagem nova sozinho.
            </p>
          </div>
          <button
            onClick={onReconectar}
            className="shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
          >
            Atualizar
          </button>
        </div>
      )}

      {naoOficiais.length > 0 && (
        <div className="flex items-start gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-xs dark:border-slate-700 dark:bg-slate-900/50">
          <ShieldAlert size={14} className="mt-0.5 shrink-0 text-gray-400 dark:text-slate-500" />
          <p className="text-gray-500 dark:text-slate-400">
            <span className="font-semibold text-gray-600 dark:text-slate-300">Canal não oficial.</span>{' '}
            {naoOficiais.map((c) => c.display_name).join(', ')} opera fora dos termos de uso do
            WhatsApp: o número pode ser banido sem aviso, e nesse caso o histórico continua aqui, mas
            o número se perde.
          </p>
        </div>
      )}
    </div>
  );
}
